import { useState, type ReactNode } from "react";
import { isFuture } from "date-fns";
import { useNavigate } from "react-router-dom";

import { useMutation } from "util/graphql";
import {
  CertificateMigrationStatus,
  NotaryProfileInvalidFields as InvalidFields,
} from "graphql_globals";
import LoadingIndicator from "common/core/loading_indicator";
import { pushNotification } from "common/core/notification_center/actions";

import type {
  NotaryProfileWizardProofCertificate_notaryProfile as NotaryProfile,
  NotaryProfileWizardProofCertificate as User,
} from "./index_fragment.graphql";
import NotaryCertificateCreation from "./create";
import ViewNotaryCertificate from "./view";
import AuthenticatorAppSetup from "./2fa";
import CreateNotaryCertificateMutation from "./create_notary_certificate_mutation.graphql";

type Props = {
  user: User;
  renderFooter?: (disabled: boolean) => ReactNode;
  isSettings?: boolean;
};
export const PROOF_CERTIFICATE_ROUTE = "proof-certificate";
type ProofCertificateType =
  | { id: "ProofCertificate"; completed: boolean; route: typeof PROOF_CERTIFICATE_ROUTE }
  | false;

export function proofCertificateSection(notaryProfile: NotaryProfile): ProofCertificateType {
  if (notaryProfile.certificateMigrationStatus === CertificateMigrationStatus.LEGACY_CERTIFICATE) {
    return false;
  }
  const fields = [InvalidFields.CERTIFICATE, InvalidFields.INVALID_CERT_EXPIRY];
  const completed = !fields.some((field) => notaryProfile.validation.invalidFields.includes(field));

  return {
    id: "ProofCertificate",
    completed,
    route: PROOF_CERTIFICATE_ROUTE,
  };
}

type State = { step: "create" } | { step: "setupQrCode"; qrCode: string } | { step: "done" };

function ProofCertificate({ user, renderFooter, isSettings }: Props) {
  const isComplete = Boolean(user.notaryProfile?.proofCertificate);
  const isValid =
    user.notaryProfile?.proofCertificate &&
    isFuture(new Date(user.notaryProfile.proofCertificate.validTo)) &&
    !user.notaryProfile.proofCertificate.revokedAt;
  const createNotaryCertificateMutateFn = useMutation(CreateNotaryCertificateMutation);
  const [state, setState] = useState<State | null>(
    isComplete ? { step: "done" } : { step: "create" },
  );
  const navigate = useNavigate();

  const generateCertificate = (pin: string) => {
    return createNotaryCertificateMutateFn({
      variables: {
        input: { notaryProfileId: user.notaryProfile!.id, pin },
      },
    })
      .then((response) => {
        setState({ step: "setupQrCode", qrCode: response.data!.createNotaryCertificate!.qrCode });
      })
      .catch((error) => {
        pushNotification({ subtype: "ERROR", message: error.message });
      });
  };

  if (!state) {
    return <LoadingIndicator />;
  }
  switch (state.step) {
    case "create":
      return (
        <>
          <NotaryCertificateCreation user={user} onGenerateCertificate={generateCertificate} />
          {renderFooter?.(!isValid)}
        </>
      );
    case "setupQrCode":
      return (
        <AuthenticatorAppSetup
          qrCode={state.qrCode}
          onComplete={() => setState({ step: "done" })}
        />
      );
    case "done": {
      return (
        <>
          <ViewNotaryCertificate
            user={user}
            onIssueNewCertificate={() => setState({ step: "create" })}
            onRenew={() => {
              setState({ step: "create" });
            }}
            onVerifyIdentity={() => {
              navigate(
                isSettings
                  ? "/settings/notary/profile/identity-verification"
                  : "/settings/notary/onboarding/IdentityVerification",
              );
            }}
          />
          {renderFooter?.(false)}
        </>
      );
    }
  }
}

export default ProofCertificate;
